import { useRef, useEffect } from 'react';

import { useWebSocket } from 'src/contexts/websocket-context';

/**
 * Hook to handle notification events from WebSocket
 *
 * Features:
 * - Plays notification sound when notification.upsert event is received
 * - Dispatches notification data to other parts of the app via window.postMessage
 * - Automatically subscribes/unsubscribes to notification events
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useNotificationHandler();
 *   return <div>...</div>;
 * }
 * ```
 */
export const useNotificationHandler = () => {
  const { subscribe } = useWebSocket();
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Initialize notification sound
    notificationSoundRef.current = new Audio('/ringtone/notification.mp3');

    const unsubscribe = subscribe('notification', (newdata) => {
      // Play notification sound
      notificationSoundRef.current?.play().catch((err) => {
        console.warn('Unable to play notification sound:', err);
      });

      // Dispatch to other parts of app via window.postMessage
      // Handle both single object and array payloads
      const payload = Array.isArray(newdata) ? newdata : [newdata];

      window.postMessage({ type: 'NOTIFICATION_UPSERT', payload }, '*');
    });

    // Cleanup function
    return () => {
      unsubscribe();
      notificationSoundRef.current = null;
    };
  }, [subscribe]);
};
